import {
  BaseRecommendRequest,
  FallbackParams,
  FbtModel,
  LookingSimilarModel,
  RecommendationsHit,
  RecommendationsRequest,
  recommendClient,
  RecommendHit,
  RelatedModel,
  TrendingItemsModel,
} from "@algolia/recommend";
import { SearchProductsResponse, ShoppingCartResponse } from "@springtree/eva-services-core";
import aa from "search-insights";

import { searchClient } from "~/components/search/search-client";
import Algolia from "~/types/algolia";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import cartUtils from "~/utils/cart-utils";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import ssrUtils from "~/utils/ssr-utils";

import Contentful from "./contentful";
import Eva from "./eva";
import Logger from "./logger/logger";

export type RecommenderParams = {
  algoliaIndexId: string;
  productID: string | undefined;
  productReferenceFields: Editorials.ProductsAlgoliaRecommender;
};
export type RecommenderEnrichment = {
  algoliaResponse: RecommendHit[];
  productDetails: any;
};

type ExtendedRecommenderParams = {
  model?: FbtModel | TrendingItemsModel | LookingSimilarModel | RelatedModel;
  objectID?: string;
  fallbackParameters?: FallbackParams;
  queryParameters?: {
    filters?: string;
    clickAnalytics?: boolean;
  };
};

export function getUserTokens() {
  let currentUserToken;
  let currentAuthenticatedUserToken;
  aa("getUserToken", {}, (err, userToken) => {
    if (err) {
      Logger.instance.error(err);
      return;
    }
    currentUserToken = userToken;
  });

  aa("getAuthenticatedUserToken", {}, (err, authenticatedUserToken) => {
    if (err) {
      Logger.instance.error(err);
      return;
    }
    currentAuthenticatedUserToken = authenticatedUserToken;
  });

  return {
    currentUserToken,
    currentAuthenticatedUserToken,
  };
}

export function algoliaAddToBag(algoliaIndexId: string, productId: string, trackingData: any, quantity = 1) {
  const { currentUserToken, currentAuthenticatedUserToken } = getUserTokens();

  let eventPayload = {
    userToken: currentUserToken,
    authenticatedUserToken: currentAuthenticatedUserToken,
    eventName: "add to cart after search",
    index: algoliaIndexId,
    objectIDs: [productId],
    objectData: [
      {
        price: trackingData?.price?.toFixed(2),
        discount: trackingData?.discount ? trackingData?.discount.toFixed(2) : undefined,
        quantity: quantity,
      },
    ],
    value: trackingData?.price,
    currency: trackingData?.currency,
  } as any;

  const currentQueryId = sessionStorage?.getItem("algolia_queryid");
  if (currentQueryId !== null && currentQueryId != "") {
    aa("addedToCartObjectIDsAfterSearch", {
      ...eventPayload,
      queryID: currentQueryId,
    });

    sessionStorage.removeItem("algolia_queryid");
    localStorage.removeItem("algolia_queryid");

    // save added products with relative queryId in localstorage for purchasedObjectIDsAfterSearch event
    const currentCartQueryIds = localStorage?.getItem("algolia_cart_queryids");
    let newAlgoliaCartQueryids = {} as any;
    if (currentCartQueryIds) {
      newAlgoliaCartQueryids = JSON.parse(currentCartQueryIds);
    }
    newAlgoliaCartQueryids[`${productId}`] = currentQueryId;
    localStorage.setItem("algolia_cart_queryids", JSON.stringify(newAlgoliaCartQueryids));
  } else {
    aa("addedToCartObjectIDs", eventPayload);
  }
}

type algoliaOrderData = {
  currency: string;
  total: number;
  items: {
    id: string;
    quantity: number;
    price: number;
    discount: number;
  }[];
};

export function algoliaPurchasedAfterSearch(algoliaIndexId: string, algoliaOrderData: algoliaOrderData) {
  const { currentUserToken, currentAuthenticatedUserToken } = getUserTokens();

  const baseDataPayload = {
    userToken: currentUserToken,
    authenticatedUserToken: currentAuthenticatedUserToken,
    eventName: "Purchase completed after search",
    index: algoliaIndexId,
    objectIDs: algoliaOrderData.items.map((item) => item.id),
    value: algoliaOrderData.total,
    currency: algoliaOrderData.currency,
  };

  const raw = localStorage?.getItem("algolia_cart_queryids");
  if (raw) {
    const currentCartQueryIds = JSON.parse(raw);
    if (currentCartQueryIds) {
      aa("purchasedObjectIDsAfterSearch", {
        ...baseDataPayload,
        objectData: algoliaOrderData.items.map((item) => ({
          queryID: currentCartQueryIds[item.id],
          price: item.price.toFixed(2),
          discount: item.discount ? Math.abs(item.discount).toFixed(2) : undefined,
          quantity: item.quantity,
        })),
      });

      localStorage.removeItem("algolia_cart_queryids");
    }
  }
}

/**
 * Maps a Next.js/Vercel locale to the corresponding Algolia locale.
 *
 * @param prefix
 * @param locale - The locale for the current request
 * @param separator - The separator you want to join the parts with, defaults to lodash ("_")
 */
export function mapLocale(prefix: string, locale: string | undefined, separator: string = "_") {
  if (!locale || !locale.includes("-")) {
    Logger.instance.error(`Invalid locale ${locale} found in request, this will likely error out.`);

    return [prefix, locale].join(separator);
  }

  return [prefix, CommonUtils.sanitizeLocale(locale)].join("_");
}

export function highlightToHtml(value: string): string {
  return value.replace(/__aa-highlight__/g, "<strong>").replace(/__\/aa-highlight__/g, "</strong>");
}

export async function getRecommender(params: Partial<RecommendationsRequest>) {
  try {
    const client = recommendClient(process.env.NEXT_PUBLIC_ALGOLIA_APPID!, process.env.NEXT_PUBLIC_ALGOLIA_APIKEY!);
    const response = await client.getRecommendations({
      requests: [params as RecommendationsRequest],
    });

    return { hits: response.results[0].hits, queryID: response.results[0].queryID };
  } catch (error) {
    Logger.instance.error(error);
  }
}

export function mergeHistWithProductsInformation(
  data: RecommendHit[],
  productsInformation: SearchProductsResponse,
  queryID?: string
): any[] {
  const previousData = [...data];
  const productList = productsInformation?.Products?.map((item: any) => {
    return {
      ...item,
      product_id: item.product_id,
      prices: { ...item },
    };
  });
  const mergedData = previousData.reduce((list: any[], item: any, idx: number) => {
    const enrichment = productList?.find((product: any) => `${product.product_id}` === `${item.objectID}`);
    if (enrichment) {
      item.__queryID = queryID;
      item.__position = idx + 1;
      list.push({ ...item, ...enrichment });
    }
    return list;
  }, []);
  return mergedData;
}

const getRecommendParams = (
  baseParams: BaseRecommendRequest,
  carouselParams: ExtendedRecommenderParams,
  carouselType: FbtModel | TrendingItemsModel | LookingSimilarModel | RelatedModel
): Partial<RecommendationsRequest> => {
  switch (carouselType) {
    case "trending-items":
      return {
        ...baseParams,
        model: carouselParams.model!,
        fallbackParameters: carouselParams.fallbackParameters,
        queryParameters: carouselParams.queryParameters,
      };
    case "looking-similar":
    case "bought-together":
    case "related-products":
      return {
        ...baseParams,
        model: carouselParams.model!,
        objectID: carouselParams.objectID,
        fallbackParameters: carouselParams.fallbackParameters,
        queryParameters: carouselParams.queryParameters,
      };
    default:
      throw new Error("Unknown carousel type");
  }
};
export async function recommenderRequest({
  algoliaIndexId,
  productReferenceFields,
  productID,
}: RecommenderParams): Promise<{ hits: RecommendationsHit[]; queryID?: string } | undefined> {
  const carouselParams = productReferenceFields;

  const carouselType = carouselParams.type as FbtModel | TrendingItemsModel | LookingSimilarModel | RelatedModel;
  const baseRecommenderParams: BaseRecommendRequest = {
    indexName: algoliaIndexId,
    threshold: carouselParams.threshold,
    maxRecommendations: carouselParams?.limit ?? 30,
  };
  const extendedParams: ExtendedRecommenderParams = {
    model: carouselType,
    objectID: productID,
    queryParameters: {
      clickAnalytics: CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_ALGOLIA_RECOMMEND_ANALYTICS),
    },
  };
  if (!!carouselParams.fallbackParameters) {
    extendedParams.fallbackParameters = {
      filters: carouselParams.fallbackParameters,
    };
  }
  if (!!carouselParams.filterParameters) {
    extendedParams.queryParameters = {
      ...extendedParams.queryParameters,
      filters: carouselParams.filterParameters,
    };
  }
  const recommenderParams = getRecommendParams(baseRecommenderParams, extendedParams, carouselType);
  const response = await getRecommender(recommenderParams);
  return response;
}

export function cartRecommenderElegibleProduct(cart: ShoppingCartResponse | undefined): string {
  if (!cart || cart?.ShoppingCart?.Lines?.length === 0) {
    return "";
  } else {
    const filteredProducts = cart.ShoppingCart?.Lines?.filter((item) => {
      const product = item.Product;
      return (
        product &&
        !cartUtils.isProductFree(item) &&
        !productUtils.isGiftCard(product?.Properties) &&
        !productUtils.isVirtualProduct(product?.Properties)
      );
    });
    //@ts-ignore
    return filteredProducts.length > 0 ? `${filteredProducts.at(-1).Product.ID}` : "";
  }
}

export function convertProductFromAgoliaToEva(item: Algolia.Product, currency: string): CrossCountry.Product {
  const currentShade = item.siblings?.find((shade) => shade.objectID === item.objectID);

  return {
    product_id: item.objectID as any,
    backend_id: item.baseBackendId ?? item.code,
    product_media: {
      media: [
        {
          id: "",
          blob: "",
          type: "image/webp",
          name: "droplet",
          url: currentShade?.droplet ?? imageUtils.transparentPixel,
        },
      ],
      primary_image: {
        id: "",
        blob: "",
        type: "image/webp",
        name: "primary",
        url:
          currentShade?.primary ||
          (Array.isArray(item.images)
            ? item.images?.find((img: { name: string }) => img.name == "primary")?.url
            : item.images?.url) ||
          imageUtils.transparentPixel,
      },
      gallery: [],
      image_count: 0,
    },
    discounts: [],
    product_types: item.product_types ?? [1],
    display_price: item.prices.value,
    original_price: item.prices.oldValue,
    display_value: item.baseProduct,
    short_description: item.description,
    slug: item.slug,
    color: item.color,
    shade_name: item.shade_name,
    shade_number: item.shade_number,
    ecomm_online_category_master: item.category,
    review_average_score: item.averageRating,
    review_total_number: item.reviewsNumber,
    currency_id: currency,
    is_available: true,
    has_stock: true,
    editorial_badges: item.editorial_badges ?? [],
    children: item.siblings?.map((shade) => {
      return {
        product_id: shade.objectID as any,
        short_description: item.description,
        display_value: item.baseProduct,
        product_types: shade.product_types ?? [1],
        discounts: [],
        product_media: {
          media: [{ id: "", blob: "", type: "image/webp", name: "droplet", url: shade.droplet }],
          primary_image: { id: "", blob: "", type: "image/webp", name: "primary", url: shade.primary },
          gallery: [],
          image_count: 0,
        },
        display_price: shade.prices.value,
        original_price: shade.prices.oldValue,
        slug: shade.slug,
        color: shade.color,
        shade_name: shade.shade_name,
        shade_number: shade.shade_number,
        currency_id: currency,
        is_available: true,
        has_stock: true,
        editorial_badges: item.editorial_badges ?? [],
      };
    }),
  };
}

async function getEssentialData(response: SearchProductsResponse) {
  if (!response.Products) {
    return undefined;
  }

  let productsWithDiscountsAndOOS: Algolia.DiscountedAndOOSProducts = {};

  const allDiscounts = response.Options?.PrefiguredDiscounts?.filter((d) => d.PromotionLabel).map((d) => ({
    DiscountID: d.DiscountID,
    PromotionLabel: d.PromotionLabel!,
    EligibleProductIDs: d.EligibleProductIDs,
  }));

  for (const product of response.Products) {
    const availability = response.Options?.AvailabilityResult?.[product.product_id as string];

    const isAvailable = availability?.Delivery?.IsAvailable || false;
    const hasStock = availability?.Delivery?.HasStock || false;

    const discounts = allDiscounts?.filter((d) => !!d.EligibleProductIDs?.find((id) => id == product.product_id));

    if (!isAvailable || !hasStock || (discounts && discounts.length > 0)) {
      Object.assign(productsWithDiscountsAndOOS, {
        [product.product_id as string]: { isAvailable, hasStock, discounts },
      });
    }
  }

  return { total: response.Total, productsWithDiscountsAndOOS };
}

export async function getDiscountedAndOOSProductsByIds(
  eva: Eva,
  ids: string[],
  sort = Eva.DEFAULT_CATEGORY_SORT,
  page = 1,
  pageSize = Constants.MAXIMUM_PAGE_SIZE
) {
  const response = await eva.getProductsByIds(ids, sort, page, pageSize).then(getEssentialData);

  let products = response?.productsWithDiscountsAndOOS;

  if (ids.length > pageSize && response) {
    const numberOfPages = Math.ceil(response.total! / pageSize);
    let promises: Promise<
      | {
          total?: number;
          productsWithDiscountsAndOOS: Algolia.DiscountedAndOOSProducts;
        }
      | undefined
    >[] = [];

    for (let page = 2; page < numberOfPages + 1; page++) {
      promises = promises.concat(eva.getProductsByIds(ids, sort, page, pageSize).then(getEssentialData));
    }

    const responses = await Promise.all(promises);
    responses.forEach((resp) => {
      if (resp) {
        products = { ...products, ...resp.productsWithDiscountsAndOOS };
      }
    });
  }

  return products;
}

/**
 * Retrieves category links for navigation based on the provided category ID.
 * This function queries Algolia's facet values to find related categories in a hierarchical structure,
 * supporting up to 4 levels of categories. It handles different category levels by adjusting the facet query
 * and falls back to parent levels when necessary.
 *
 * @param contentful - The Contentful service instance used to extract and format hierarchical category data.
 * @param algoliaIndexId - The Algolia index ID where the search for facet values is performed.
 * @param maxFacetHits - The maximum number of facet hits to retrieve from Algolia.
 * @param categoryId - The category ID to get related links for. If undefined or empty, returns an empty array.
 * @returns An array of category links extracted from the hierarchical category data.
 */
export async function getCategoryLinks(
  contentful: Contentful,
  algoliaIndexId: string,
  maxFacetHits: number,
  categoryId?: string
) {
  if (!categoryId) {
    return [];
  }

  try {
    const MAX_CATEGORY_LEVEL = 4;
    const categoryParts = categoryId.split("_");
    const isLastLevelCategory = categoryParts.length >= MAX_CATEGORY_LEVEL;
    const categoryLevel = isLastLevelCategory ? MAX_CATEGORY_LEVEL : categoryParts.length + 1;
    const facetName = `categories.lvl${categoryLevel}`;
    const facetQuery = isLastLevelCategory ? categoryParts.slice(0, -1).join("_") : categoryId;

    let facetValues: string[];

    // Query Algolia for facet values at the determined level
    const categoryFacetValuesResponse = searchClient
      .initIndex(algoliaIndexId)
      .searchForFacetValues(facetName, facetQuery, { maxFacetHits });

    if (isLastLevelCategory) {
      // For deepest level categories, get sibling categories at the same level
      facetValues = (await categoryFacetValuesResponse).facetHits
        .map((hit) => hit.value)
        .filter((value) => value !== categoryId);
    } else if (categoryLevel > 1) {
      // For middle-level categories, try to get subcategories first
      const facetName = `categories.lvl${categoryLevel - 1}`;
      const facetQuery = categoryParts.slice(0, -1).join("_");

      // Also query the parent level as fallback
      const topLevelCategoryFacetValuesResponse = searchClient
        .initIndex(algoliaIndexId)
        .searchForFacetValues(facetName, facetQuery, { maxFacetHits });

      const [categoryFacetValues, topLevelCategoryFacetValues] = await Promise.all([
        categoryFacetValuesResponse,
        topLevelCategoryFacetValuesResponse,
      ]);

      // Use subcategories if available, otherwise fall back to siblings at parent level
      facetValues =
        categoryFacetValues.facetHits.length > 0
          ? categoryFacetValues.facetHits.map((hit) => hit.value)
          : topLevelCategoryFacetValues.facetHits.map((hit) => hit.value).filter((value) => value !== categoryId);
    } else {
      // For top-level categories, get all subcategories
      facetValues = (await categoryFacetValuesResponse).facetHits.map((hit) => hit.value);
    }

    return ssrUtils.extractHierarchicalCategoryData(contentful, facetValues);
  } catch (error) {
    Logger.instance.error(`Error fetching facet values for categoryId ${categoryId}`, error);
    return [];
  }
}
